import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import { useAppDispatch } from "../store/hooks";
import { RootState } from "../store";
import {
  initTeamSession,
  fetchTeamSession,
  updateTeamSessionState,
  toggleTask,
  deleteTask,
  addNote,
  addCheckin,
  // synchronous helpers left for optimistic use if needed
  // addNote,
  // deleteNote,
  toggleMemberMute,
  // async thunks for notes
  fetchTeamNotes,
  createTeamNote,
  deleteTeamNote,
  updateTeamNote,
  // async thunk for RTC token
  fetchRtcToken,
  fetchTeamTasks,
  fetchTeamCheckins,
} from "../store/slices/teamFocusSessionSlice";
import { Note } from "../store/slices/teamFocusSession/types";
import { MdAccessTime, MdMonitor } from "react-icons/md";
import { toast } from "react-toastify";
import {
  TaskModal,
  NoteModal,
  CheckInModal,
} from "../components/page-components/Dashboard";

import TeamModeCheckins from "../components/page-components/TeamFocusSession/TeamModeCheckins";
import { useTeamSessionTimer } from "../hooks/useTeamSessionTimer";
import ParticipantsSidebar from "../components/page-components/TeamFocusSession/ParticipantsSidebar";
import SessionHeader from "../components/page-components/TeamFocusSession/SessionHeader";
import TimerDisplay from "../components/page-components/TeamFocusSession/TimerDisplay";
import ScreenShareView from "../components/page-components/TeamFocusSession/ScreenShareView";
import InvalidSession from "../components/page-components/TeamFocusSession/InvalidSession";
import FabMenu from "../components/page-components/TeamFocusSession/FabMenu";
import SessionTabs from "../components/page-components/TeamFocusSession/SessionTabs";

const TeamFocusSessionPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // preloaded session passed via navigation state (dashboard create path)
  const preloaded: any | undefined = (location.state as any)?.teamSession;

  const session = useSelector((state: RootState) =>
    teamId ? state.teamFocusSessions.sessions[teamId] : null,
  );

  useTeamSessionTimer(teamId, session);

  // local UI state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [view, setView] = useState<"timer" | "screenshare">("timer");
  const [isScreenSharing, setIsScreenSharing] = useState(true);

  const hasInitialSession = !!session || !!(preloaded && preloaded.roomId === teamId);
  const [loadingSession, setLoadingSession] = useState(!hasInitialSession);

  // Modal state
  const [activeModal, setActiveModal] = useState<
    "task" | "note" | "checkin" | null
  >(null);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [editingNote, setEditingNote] = useState<Note | null>(null);

  // load or initialize session data
  useEffect(() => {
    if (!teamId) return;
    if (preloaded && preloaded.roomId === teamId) {
      // populate store with preloaded data
      dispatch(initTeamSession({ teamId, initialData: preloaded }));
    } else if (!session) {
      setLoadingSession(true);
      dispatch(fetchTeamSession(teamId))
        .unwrap()
        .catch(() => {
          // ignore; invalid session handling below
        })
        .finally(() => setLoadingSession(false));
    }
  }, [teamId, preloaded, session, dispatch]);

  const rtcTokenFetchedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!teamId) return;
    if (rtcTokenFetchedRef.current === teamId) return; // already fetched for this room
    dispatch(fetchRtcToken(teamId));
    rtcTokenFetchedRef.current = teamId;
  }, [teamId, dispatch]);

  // fetch notes once per team; use a ref to avoid double-fetches
  const notesFetchedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!teamId) return;
    if (notesFetchedRef.current === teamId) return; // already loaded for this room
    dispatch(fetchTeamNotes({ teamId }));
    notesFetchedRef.current = teamId;
  }, [teamId, dispatch]);

  const tasksFetchedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!teamId) return;
    if (tasksFetchedRef.current === teamId) return;
    dispatch(fetchTeamTasks(teamId));
    tasksFetchedRef.current = teamId;
  }, [teamId, dispatch]);

  const checkinsFetchedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!teamId) return;
    if (checkinsFetchedRef.current === teamId) return;
    dispatch(fetchTeamCheckins(teamId));
    checkinsFetchedRef.current = teamId;
  }, [teamId, dispatch]);

  // reset guards whenever teamId actually changes (fresh room)
  useEffect(() => {
    notesFetchedRef.current = null;
    rtcTokenFetchedRef.current = null;
    tasksFetchedRef.current = null;
    checkinsFetchedRef.current = null;
  }, [teamId]);

  const hasSession =
    !!teamId && (session || (preloaded && preloaded.roomId === teamId));
  const isSessionInvalid = !!teamId && !hasSession && !loadingSession;

  // choose whichever data is available and merge with defaults so arrays never end up undefined
  const sess = {
    tasks: [],
    notes: [],
    reminders: [],
    teamMembers: [],
    // Spread what we actually have (session from store or preloaded state)
    ...(session || preloaded),
  } as NonNullable<typeof session>;

  if (!teamId || loadingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8F0]">
        Loading Session...
      </div>
    );
  }

  if (isSessionInvalid) {
    return <InvalidSession />;
  }

  const handleStartSession = () => {
    dispatch(
      updateTeamSessionState({
        teamId,
        updates: {
          sessionStarted: true,
          sessionPaused: false,
          sessionCompleted: false,
        },
      }),
    );
  };

  const handlePauseSession = () => {
    dispatch(
      updateTeamSessionState({ teamId, updates: { sessionPaused: true } }),
    );
  };

  const handleResumeSession = () => {
    dispatch(
      updateTeamSessionState({ teamId, updates: { sessionPaused: false } }),
    );
  };

  const handleEndSession = () => {
    const confirm = window.confirm(
      "Are you sure you want to end this session early?",
    );
    if (confirm) {
      dispatch(
        updateTeamSessionState({
          teamId,
          updates: { sessionCompleted: true, sessionStarted: false },
        }),
      );
      navigate("/dashboard");
    }
  };

  const openModal = (type: "task" | "note" | "checkin") => {
    if (type === "task") {
      setEditingTask(null); // Clear editing task when opening new task block
    }
    setActiveModal(type);
  };

  const handleEditTask = (task: any) => {
    setEditingTask(task);
    setActiveModal("task");
  };

  const handleEditNote = (note: Note) => {
    setEditingNote(note);
    setActiveModal("note");
  };

  const handleCheckInAdd = (title: string, checkinTime: string) => {
    if (teamId && title.trim() && checkinTime) {
      dispatch(
        addCheckin({
          teamId,
          checkin: {
            id: Date.now().toString(),
            title: title.trim(),
            checkinTime,
            created_by: "current-user",
            is_public: false,
            roomId: teamId,
          },
        }),
      );
      toast.success("Check-in added successfully!");
    }
  };

  return (
    <div className="flex flex-col h-screen min-h-screen bg-[#FFF8F0]">
      <LiveKitRoom
        className="flex-1 flex flex-col min-h-0"
        serverUrl={import.meta.env.VITE_LIVEKIT_URL}
        token={session?.rtcToken ?? ''}
        connect={!!session?.rtcToken}
        video={false}
        audio={false}
      >
        <RoomAudioRenderer />
        <SessionHeader
          teamId={teamId}
          goal={session?.goal || ""}
          hasToken={!!session?.rtcToken}
          isMuted={session?.teamMembers[0]?.isMuted ?? true}
          onToggleMute={() =>
            dispatch(
              toggleMemberMute({
                teamId,
                memberId: "1",
                isMuted: !(session?.teamMembers[0]?.isMuted ?? true),
              }),
            )
          }
          onEndCall={handleEndSession}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          isSidebarOpen={isSidebarOpen}
        />
        <div className="flex-1 flex overflow-hidden relative">
          {isSidebarOpen && (
            <div
              className="absolute inset-0 bg-black/20 z-20 sm:hidden"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}
          <div
            className={`${isSidebarOpen ? "block absolute z-30 h-full" : "hidden"} sm:block sm:relative h-full transition-all`}
          >
            <ParticipantsSidebar participants={session?.teamMembers || []} hasToken={!!session?.rtcToken} />
          </div>
          <div className="flex-1 overflow-hidden w-full">
            <div className="w-full max-w-full mx-auto p-4 sm:p-6 h-full flex flex-col">
              <div className="bg-white border border-gray-200/60 rounded-2xl shadow-sm flex flex-col min-h-0 flex-1 relative overflow-hidden">
                {view === "timer" ? (
                  <TimerDisplay
                    timeLeft={sess.sessionTimeLeft}
                    sessionStarted={sess.sessionStarted || false}
                    sessionPaused={sess.sessionPaused || false}
                    sessionCompleted={sess.sessionCompleted || false}
                    onStart={handleStartSession}
                    onPause={handlePauseSession}
                    onResume={handleResumeSession}
                    onEnd={handleEndSession}
                    className="rounded-t-2xl"
                  >
                    <SessionTabs
                      teamId={teamId}
                      session={sess}
                      onEditTask={handleEditTask}
                      onEditNote={handleEditNote}
                    />
                  </TimerDisplay>
                ) : (
                  <div className="flex-1 flex flex-col min-h-0">
                    <ScreenShareView />
                  </div>
                )}

                {isScreenSharing && (
                  <div className="absolute top-4 right-4 flex gap-2 z-10">
                    <button
                      onClick={() => setView("timer")}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium ${view === "timer"
                        ? "bg-orange-600 text-white shadow-sm"
                        : "bg-white/90 text-gray-600 hover:bg-gray-50 border border-gray-200/60 shadow-sm"
                        }`}
                    >
                      <MdAccessTime className="w-4 h-4" />
                      <span>Timer</span>
                    </button>
                    <button
                      onClick={() => setView("screenshare")}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium ${view === "screenshare"
                        ? "bg-orange-600 text-white shadow-sm"
                        : "bg-white/90 text-gray-600 hover:bg-gray-50 border border-gray-200/60 shadow-sm"
                        }`}
                    >
                      <MdMonitor className="w-4 h-4" />
                      <span>Screen</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </LiveKitRoom>

      <FabMenu onOpenModal={openModal} />

      {/* Modals */}
      <TaskModal
        isOpen={activeModal === "task"}
        onClose={() => {
          setActiveModal(null);
          setEditingTask(null);
        }}
        initialTask={editingTask}
      />
      <NoteModal
        isOpen={activeModal === "note"}
        onClose={() => {
          setActiveModal(null);
          setEditingNote(null);
        }}
        initialNote={editingNote}
      />
      <CheckInModal
        isOpen={activeModal === "checkin"}
        onClose={() => setActiveModal(null)}
        onAdd={handleCheckInAdd}
      />
    </div>
  );
};

export default TeamFocusSessionPage;
